import type { FunctionComponent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import type { IHistoryGame, ISingleHistory } from '../../../api/fetchers/history/types';
import { useSingleHistoryFetcher } from '../../../store/apiHooks/useHistoryFetcher';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { Loader } from '../../loader';
import { useHistoryNavigation } from '../hooks/useHistoryNavigation';
import { HistoryMessage } from '../message';

import { HistoryBetInfo } from './betInfo';
import { HistoryGameInfo } from './gameInfo';

import styles from './HistorySingle.module.css';

export interface HistorySingleProps {
  game: IHistoryGame;
  inNavDrawer?: boolean;
}

export const HistorySingleConnected: FunctionComponent<HistorySingleProps> = ({
  game,
  inNavDrawer,
}: HistorySingleProps) => {
  const [data, setData] = useState<ISingleHistory | null>();
  const [error, setError] = useState<Error>();
  const { handleDetailsLoaded } = useHistoryNavigation();
  const config = useConfigSlice.tableConfig();
  const singleHistoryFetcher = useSingleHistoryFetcher();

  const fetchData = useCallback((): void => {
    singleHistoryFetcher(game.gameId)
      ?.unwrap()
      .then((data: ISingleHistory) => {
        setData(data);
        handleDetailsLoaded();
      })
      .catch((error: Error) => {
        setError(error);
      });
  }, [game.gameId, handleDetailsLoaded, singleHistoryFetcher]);

  // TODO: Check if game details data should be moved to Redux store
  // As History component will be moved to separate package without Redux ctx
  useEffect(() => {
    fetchData();
    return () => {
      setData(null);
    };
  }, [fetchData, singleHistoryFetcher]);

  const wrapperStyles = `${styles.HistorySingle} ${
    !inNavDrawer ? styles.HistorySingleResponsive : styles.HistorySingle_drawer
  }`;
  if (error) {
    return (
      <div className={wrapperStyles}>
        <div className={styles.centered}>
          <HistoryMessage title="ERROR_TITLE" message="ERROR_TEXT" />
          <div className={styles.loadMore}>
            <Button tk="RELOAD" variant={ButtonVariant.MAIN} loading={false} onClick={(): void => fetchData()} />
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <Loader />;
  return (
    <div className={wrapperStyles}>
      <HistoryGameInfo
        type={game.type}
        bets={data.bets}
        game={data.game}
        seats={data.seats}
        inNavDrawer={!!inNavDrawer}
      />
      <HistoryBetInfo
        type={game.type}
        bets={data.bets}
        game={data.game}
        seats={data.seats}
        config={config}
        inNavDrawer={!!inNavDrawer}
      />
    </div>
  );
};
